/**
 * WEB-3879 — висячая ссылка на вариант ответа своего экрана в кабинете.
 * Зеркало `pruneDanglingOptionRefs` сервера
 * (`backend/src/ai-funnels/operations/option-refs.ts`): id в поле ссылок
 * (`BLOCK_OPTION_REF_FIELDS`, сегодня `micro_feedback.revealOptionIds`),
 * которого нет среди вариантов экрана, снимается; опустевший перечень — поле
 * снято и `revealAfterAnswer: true` (иначе подсказка открылась бы сразу, до
 * ответа). Без висячих — тот же массив блоков: пустой ход не попадает в
 * историю отмены (шрам WEB-1532).
 *
 * Варианты экрана — как у сервера (`resolveBlockOptions`): у `image_choice` —
 * `imageChoiceOptions`, если это массив, иначе `options`; у прочих —
 * `options`; в счёт — варианты-объекты со строковым `id`. Оба конца гоняют
 * общий файл случаев `option-refs-prune.cases.json`.
 */
import { BLOCK_OPTION_REF_FIELDS } from './duplicate';

type Json = Record<string, unknown>;

const isObject = (v: unknown): v is Json =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Варианты блока: живой список (у картинок — свой) или общий. */
function blockOptions(block: Json): unknown[] {
  if (block.type === 'image_choice' && Array.isArray(block.imageChoiceOptions))
    return block.imageChoiceOptions;
  return Array.isArray(block.options) ? block.options : [];
}

/** id вариантов всех блоков экрана. */
function screenOptionIds(blocks: readonly unknown[]): Set<string> {
  const ids = new Set<string>();
  for (const block of blocks) {
    if (!isObject(block)) continue;
    for (const o of blockOptions(block))
      if (isObject(o) && typeof o.id === 'string') ids.add(o.id);
  }
  return ids;
}

/**
 * Снять со всех блоков экрана ссылки на варианты, которых на экране нет.
 * Без висячих ссылок возвращает тот же массив блоков.
 */
export function pruneDanglingOptionRefs<T>(blocks: T[]): T[] {
  const ids = screenOptionIds(blocks);
  let changed = false;
  const next = blocks.map((block) => {
    if (!isObject(block)) return block;
    let out: Json | undefined;
    for (const field of BLOCK_OPTION_REF_FIELDS) {
      const value = block[field];
      if (!Array.isArray(value)) continue;
      const kept = value.filter((e) => typeof e === 'string' && ids.has(e));
      if (kept.length === value.length) continue;
      out = { ...(out ?? block) };
      if (kept.length > 0) {
        out[field] = kept;
      } else {
        delete out[field];
        if (field === 'revealOptionIds') out.revealAfterAnswer = true;
      }
    }
    if (!out) return block;
    changed = true;
    return out as T;
  });
  return changed ? next : blocks;
}
